// React imports
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import { formatDisplayDateTime } from '../utils/date.js';
import axios from 'axios';

// Material UI imports
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { useTheme, alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import { green, red, blue, purple, orange, teal, indigo, pink } from '@mui/material/colors';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelopeOpenText, faBuilding, faClipboard, faBell, faDotCircle, faBullseyeArrow, faCheckCircle, faTimesCircle, faAlarmClock,
    faPaperPlane, faRedo, faExclamationTriangle
} from '@fortawesome/pro-solid-svg-icons';

// Local imports
import email from '../dashboard/utils/email';

function SidebarNotifications(props) {
    const navigate = useNavigate();
    const theme = useTheme();
    const { org, opportunities, tradelines, notifications, onUpdateNotification, accounts, suppliers,
        accesstoken, emailLoginInfo, emailConversations } = props;
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
    const [extraConversations, setExtraConversations] = useState([]);
    const [failedConversations, setFailedConversations] = useState([]);

    useEffect(() => {
        if (emailLoginInfo?.status === 'connected') {
            const emailNotifications = notifications.filter(n => n.type === 'email-note-mention');
            const conversationIds = emailNotifications.map(n => n.conversationId).filter(c => !_.some(emailConversations?.flat(), cc => cc.id === c || cc.globalMessageId === c));

            (async () => {
                const retrieve = await Promise.allSettled(conversationIds.map(async (conversationId) => email.getMessagesForConversation({ org, internetMessageId: conversationId, tokens: emailLoginInfo.tokens })));
                retrieve.forEach((r, i) => {
                    if (r.status === 'fulfilled') {
                        setExtraConversations(prev => _.uniqBy([...prev, r.value], 'id'));
                        setFailedConversations(prev => _.without(prev, conversationIds[i]));
                    } else {
                        setFailedConversations(prev => _.uniq([...prev, conversationIds[i]]));
                    }
                });
            })();
        }
    }, [emailLoginInfo?.status, emailConversations, notifications, org]);

    const onNotificationsOpen = (event) => {
        setNotificationsAnchorEl(event.currentTarget);
        setNotificationsOpen(true);
    };

    const onNotificationsClose = () => {
        setNotificationsOpen(false);
    };

    const badge = _(notifications || []).filter(n => !n.read).value().length;

    return (
        <>
            <List sx={{ padding: '4px 0px' }}>
                <ListItem
                    button
                    sx={{
                        color: theme.palette.text.secondary,
                        padding: '4px 8px',
                        margin: '2px 4px',
                        borderRadius: '4px',
                        border: '1px solid transparent',
                        fontSize: '11px',
                        minHeight: '32px',
                        '&:hover': {
                            border: '1px solid',
                            borderColor: alpha(theme.palette.text.secondary, 0.2),
                            backgroundColor: alpha(theme.palette.text.secondary, 0.05),
                        },
                    }}
                    onClick={onNotificationsOpen}
                >
                    <ListItemIcon sx={{ color: theme.palette.text.secondary, minWidth: '32px', '& svg': { fontSize: '14px' } }}>
                        <Badge badgeContent={badge} color='error'>
                            <FontAwesomeIcon icon={faBell} />
                        </Badge>
                    </ListItemIcon>
                    <ListItemText primary='Notifications' primaryTypographyProps={{ sx: { fontSize: '11px', color: theme.palette.text.secondary, fontWeight: 400 } }} />
                </ListItem>
            </List>
            <Popover
                open={notificationsOpen}
                anchorEl={notificationsAnchorEl}
                onClose={onNotificationsClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ minWidth: 420, maxWidth: 520 }}>
                    <Notifications
                        notifications={notifications}
                        opportunities={opportunities}
                        emailMessages={[...(emailConversations?.flat() || []), ...(extraConversations?.flat() || [])]}
                        failedConversations={failedConversations}
                        accounts={accounts}
                        suppliers={suppliers}
                        tradelines={tradelines}
                        navigate={(path) => navigate(`/organization/${org.alias}${path}`)}
                        onClose={onNotificationsClose}
                        onUpdateNotification={onUpdateNotification}
                        accesstoken={accesstoken}
                    />
                </Box>
            </Popover>
        </>
    );
}

// Separate component for Outgoing Emails
export function SidebarOutgoing({ org, accesstoken }) {
    const theme = useTheme();
    const [outgoingOpen, setOutgoingOpen] = useState(false);
    const [outgoingAnchorEl, setOutgoingAnchorEl] = useState(null);
    const [outgoingEmails, setOutgoingEmails] = useState([]);
    const [outgoingLoading, setOutgoingLoading] = useState(false);

    // Track emails we've seen as pending/processing (eligible for 'sent' notification)
    const pendingEmailsRef = useRef(new Set());
    // Track emails we've already dispatched 'sent' events for
    const sentNotifiedRef = useRef(new Set());

    // Fetch outgoing emails
    const fetchOutgoingEmails = useCallback(async () => {
        if (!org?.integrations?.email?.enabled || !accesstoken) return;

        setOutgoingLoading(true);
        try {
            const emails = await email.getQueuedEmails({ org, monadaAccesstoken: accesstoken, limit: 20 });

            // Check for status transitions
            emails.forEach(emailItem => {
                // Track pending/processing emails
                if (emailItem.status === 'pending' || emailItem.status === 'processing') {
                    pendingEmailsRef.current.add(emailItem.id);
                }

                // Only notify for emails that transitioned from pending/processing to sent
                if (emailItem.status === 'sent' &&
                    pendingEmailsRef.current.has(emailItem.id) &&
                    !sentNotifiedRef.current.has(emailItem.id)) {

                    // Mark as notified
                    sentNotifiedRef.current.add(emailItem.id);
                    pendingEmailsRef.current.delete(emailItem.id);

                    console.log('[SidebarOutgoing] Email sent:', emailItem);
                    window.dispatchEvent(new CustomEvent('email-sent', {
                        detail: {
                            id: emailItem.id,
                            opportunityId: emailItem.opportunityId,
                            conversationId: emailItem.conversationId,
                            internetMessageId: emailItem.internetMessageId,
                            to: emailItem.to
                        }
                    }));
                }
            });

            setOutgoingEmails(emails);
        } catch (e) {
            console.error('[SidebarOutgoing] Error fetching outgoing emails:', e);
        } finally {
            setOutgoingLoading(false);
        }
    }, [org, accesstoken]);

    // Poll for outgoing email updates when popover is open
    useEffect(() => {
        if (outgoingOpen && org?.integrations?.email?.enabled) {
            fetchOutgoingEmails();
            const interval = setInterval(fetchOutgoingEmails, 5000);
            return () => clearInterval(interval);
        }
    }, [outgoingOpen, fetchOutgoingEmails, org?.integrations?.email?.enabled]);

    // Listen for new emails being queued and add them immediately
    useEffect(() => {
        if (!org?.integrations?.email?.enabled) return;

        const handleEmailQueued = (event) => {
            console.log('[SidebarOutgoing] Email queued:', event.detail);
            // Add the new email to the list immediately
            const newEmail = {
                id: event.detail.id,
                status: event.detail.status || 'pending',
                subject: event.detail.subject || '(No subject)',
                to: event.detail.to || [],
                createdAt: event.detail.createdAt || Date.now(),
                retryCount: 0,
                provider: event.detail.provider,
                isReply: event.detail.isReply,
                opportunityId: event.detail.opportunityId
            };

            // Track this as a pending email so we can notify when it's sent
            pendingEmailsRef.current.add(newEmail.id);

            setOutgoingEmails(prev => [newEmail, ...prev.filter(e => e.id !== newEmail.id)]);

            // Also fetch to ensure we're in sync
            setTimeout(fetchOutgoingEmails, 2000);
        };

        window.addEventListener('email-queued', handleEmailQueued);
        return () => window.removeEventListener('email-queued', handleEmailQueued);
    }, [fetchOutgoingEmails, org?.integrations?.email?.enabled]);

    // Initial fetch on mount
    useEffect(() => {
        if (org?.integrations?.email?.enabled) {
            fetchOutgoingEmails();
        }
    }, [org?.integrations?.email?.enabled]);

    // Periodic check for pending emails even when popover is closed
    useEffect(() => {
        if (!org?.integrations?.email?.enabled) return;

        const pendingEmails = outgoingEmails.filter(e => e.status === 'pending' || e.status === 'processing');
        
        // Only poll if there are pending emails
        if (pendingEmails.length === 0) return;

        // Poll every 5 seconds to check if pending emails have changed status
        const interval = setInterval(() => {
            fetchOutgoingEmails();
        }, 5000);

        return () => clearInterval(interval);
    }, [outgoingEmails, fetchOutgoingEmails, org?.integrations?.email?.enabled]);

    const onOutgoingOpen = (event) => {
        event.stopPropagation(); // Prevent parent ListItem click
        setOutgoingAnchorEl(event.currentTarget);
        setOutgoingOpen(true);
    };

    const onOutgoingClose = () => {
        setOutgoingOpen(false);
    };

    const pendingEmails = outgoingEmails.filter(e => e.status === 'pending' || e.status === 'processing').length;

    if (!org?.integrations?.email?.enabled) {
        return null;
    }

    return (
        <>
            <Tooltip title="Outgoing Emails">
                <IconButton
                    onClick={onOutgoingOpen}
                    sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                        },
                    }}
                >
                    <Badge badgeContent={pendingEmails} color='primary'>
                        <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '16px' }} />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popover
                open={outgoingOpen}
                anchorEl={outgoingAnchorEl}
                onClose={onOutgoingClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ minWidth: 420, maxWidth: 520 }}>
                    <OutgoingEmails
                        emails={outgoingEmails}
                        loading={outgoingLoading}
                        org={org}
                        accesstoken={accesstoken}
                        onRetry={fetchOutgoingEmails}
                    />
                </Box>
            </Popover>
        </>
    );
}

export default SidebarNotifications;

function OutgoingEmails({ emails, loading, org, accesstoken, onRetry }) {
    const theme = useTheme();

    const handleRetry = async (emailId) => {
        try {
            await email.retryEmail({ org, emailId, monadaAccesstoken: accesstoken });
            onRetry();
        } catch (e) {
            console.error('[OutgoingEmails] Error retrying email:', e);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
            case 'processing':
                return <CircularProgress size={16} />;
            case 'sent':
                return <FontAwesomeIcon icon={faCheckCircle} style={{ color: green[500] }} />;
            case 'error':
                return <FontAwesomeIcon icon={faTimesCircle} style={{ color: red[500] }} />;
            default:
                return <FontAwesomeIcon icon={faPaperPlane} />;
        }
    };

    const getStatusText = (emailItem) => {
        const recipients = (emailItem.to || []).map(r => r.address || r).join(', ');
        switch (emailItem.status) {
            case 'pending':
                return `Queued to ${recipients}`;
            case 'processing':
                return `Sending to ${recipients}...`;
            case 'sent':
                return `Sent to ${recipients}`;
            case 'error':
                return emailItem.error || `Failed to send to ${recipients}`;
            default:
                return `To ${recipients}`;
        }
    };

    if (loading && emails.length === 0) {
        return (
            <Box sx={{ padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={18} />&nbsp;Loading outgoing emails...
            </Box>
        );
    }

    if (emails.length === 0) {
        return (
            <Box sx={{ padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                    No outgoing emails
                </Typography>
            </Box>
        );
    }

    return (
        <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
            {emails.map(emailItem => (
                <ListItem
                    key={emailItem.id}
                    sx={{ py: 0.5 }}
                    secondaryAction={
                        emailItem.status === 'error' && (
                            <Tooltip title="Retry sending">
                                <IconButton
                                    edge='end'
                                    size="small"
                                    onClick={() => handleRetry(emailItem.id)}
                                >
                                    <FontAwesomeIcon icon={faRedo} style={{ fontSize: '12px' }} />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                >
                    <ListItemIcon sx={{ minWidth: '36px' }}>
                        {getStatusIcon(emailItem.status)}
                    </ListItemIcon>
                    <ListItemText
                        primary={emailItem.subject || '(No subject)'}
                        secondary={
                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                <span>{getStatusText(emailItem)}</span>
                                <span style={{ fontSize: '9px', color: theme.palette.text.disabled }}>
                                    {dayjs(emailItem.createdAt).format('MMM D, YYYY h:mm A')}
                                    {emailItem.retryCount > 0 && ` (${emailItem.retryCount} retries)`}
                                </span>
                            </Box>
                        }
                        primaryTypographyProps={{ sx: { fontSize: '11px', fontWeight: 500 } }}
                        secondaryTypographyProps={{ sx: { fontSize: '10px' }, component: 'div' }}
                    />
                </ListItem>
            ))}
        </List>
    );
}

function Notifications(props) {
    const { notifications, opportunities, accounts, suppliers, tradelines, navigate,
        onClose, onUpdateNotification, accesstoken, emailMessages,
        failedConversations
    } = props;
    const theme = useTheme();

    if (!notifications || !opportunities || !accounts || !tradelines) return (
        <Box sx={{ padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={18} />&nbsp;Loading notifications...
        </Box>
    )

    if (_.isEmpty(notifications)) return (
        <Box sx={{ padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>No notifications</Typography>
        </Box>
    )

    function getIcon(notification) {
        if (notification.type === 'email-note-mention') {
            return <FontAwesomeIcon icon={faEnvelopeOpenText} />;
        }

        if (notification.type === 'opportunity-mention') {
            return <FontAwesomeIcon icon={faEnvelopeOpenText} />;
        }

        if (notification.type === 'supplier-mention') {
            return <FontAwesomeIcon icon={faClipboard} />;
        }

        if (notification.type === 'account-mention') {
            return <FontAwesomeIcon icon={faBuilding} />;
        }

        if (notification.type === 'rates-import-parsing-done') {
            return <FontAwesomeIcon icon={faClipboard} />;
        }

        if (notification.type === 'rates-import-comment') {
            return <FontAwesomeIcon icon={faClipboard} />;
        }

        if (notification.type === 'tradeline-mention') {
            return <FontAwesomeIcon icon={faBullseyeArrow} />;
        }

        if (notification.type === 'opportunity-accepted') {
            return <FontAwesomeIcon icon={faCheckCircle} />;
        }

        if (notification.type === 'opportunity-rejected') {
            return <FontAwesomeIcon icon={faTimesCircle} />;
        }

        if (notification.type === 'rfq-reply') {
            return <FontAwesomeIcon icon={faEnvelopeOpenText} />;
        }
        if (notification.type === 'reminder-due') {
            return <FontAwesomeIcon icon={faAlarmClock} />;
        }
        if (notification.type === 'task-assigned') {
            return <FontAwesomeIcon icon={faClipboard} />;
        }
        if (notification.type === 'opportunity-owner-changed') {
            return <FontAwesomeIcon icon={faPaperPlane} />;
        }
    }

    function getPrimaryText(notification) {
        if (!notification) return null;
        if (notification.type === 'opportunity-mention') {
            const opportunity = opportunities.find(o => o.id === notification.opportunityId);
            if (!opportunity) {
                return <span>You were mentioned by <b>{notification.createdBy || 'Someone'}</b> in an opportunity you don't have access to.</span>;
            }
            const account = accounts.find(a => a.id === opportunity.account?.id);
            const update = opportunity.updates.find(u => u.created === notification.created);
            if (!update || !account) {
                return <span>You were mentioned by <b>{notification.createdBy || 'Someone'}</b> in an opportunity.</span>;
            }
            return <span>You were mentioned by <b>{update.createdBy}</b> in an opportunity created for <b>{account.name}</b>.</span>;
        }

        if (notification.type === 'account-mention') {
            const account = accounts.find(a => a.id === notification.accountId);
            const update = account.updates.find(u => u.created === notification.created);
            return <span>You were mentioned by <b>{update.createdBy}</b> in <b>{account.name}</b> account page.</span>;
        }

        if (notification.type === 'supplier-mention') {
            const supplier = suppliers.find(s => s.id === notification.supplierId);
            const update = supplier.updates.find(u => u.created === notification.created);
            return <span>You were mentioned by <b>{update.createdBy}</b> in <b>{supplier.name}</b> supplier page.</span>;
        }

        if (notification.type === 'rates-import-parsing-done') {
            return <span>Finished parsing rates from <b>{notification.supplier}</b>.</span>;
            }

        if (notification.type === 'rates-import-comment') {
            return <span>You were mentioned by <b>{notification.createdBy || 'Someone'}</b> in a rates import comment.</span>;
        }

        if (notification.type === 'tradeline-mention') {
            const tradeline = tradelines.find(t => t.id === notification.tradelineId);
            if (!tradeline) {
                return <span>You were mentioned by <b>{notification.createdBy || 'Someone'}</b> in a tradeline you don't have access to.</span>;
            }
            const update = tradeline.updates.find(u => u.created === notification.created);
            if (!update) {
                return <span>You were mentioned by <b>{notification.createdBy || 'Someone'}</b> in a tradeline.</span>;
            }
            return <span>You were mentioned by <b>{update.createdBy}</b> in a tradeline "<b>{tradeline.title}</b>".</span>;
        }

        if (notification.type === 'email-note-mention') {
            const message = emailMessages?.find(m => m?.id === notification.conversationId || m?.globalMessageId === notification.conversationId);
            if (message) {
                return <span>You were mentioned by <b>{notification.createdBy || 'Someone'}</b> in an email note for email <b>"{message.subject}"</b>.</span>;
            }
            if (failedConversations?.includes(notification.conversationId)) {
                return <span>You were mentioned by <b>{notification.createdBy || 'Someone'}</b> in an email note, but you can't access the message.</span>;
            }
            return <span>You were mentioned by <b>{notification.createdBy || 'Someone'}</b> in an email note.</span>;
        }

        if (notification.type === 'opportunity-accepted') {
            return <span>Your opportunity was accepted by <b>{notification.createdBy || 'Someone'}</b>.</span>;
        }

        if (notification.type === 'opportunity-rejected') {
            return <span>Your opportunity was rejected by <b>{notification.createdBy || 'Someone'}</b>.</span>;
        }

        if (notification.type === 'rfq-reply') {
            return <span><b>{notification.supplierName || 'A supplier'}</b> replied to your rate request. You can now move forward with your quote for <b>{notification.accountName || 'the account'}</b>.</span>;
        }

        if (notification.type === 'reminder-due') {
            return <span>Your reminder for <b>{notification.accountName || 'the account'}</b> ("{notification.reminderDescription}") is due.</span>;
        }

        if (notification.type === 'opportunity-owner-changed') {
            const opportunity = opportunities.find(o => o.id === notification.opportunityId);
            const account = opportunity ? accounts.find(a => a.id === opportunity.account?.id) : null;
            const oppName = account?.name || 'an opportunity';
            const actor = notification.createdBy || 'Someone';
            return <span><b>{actor}</b> just moved <b>{oppName}</b> to you.</span>;
        }
    }

    function getSecondaryText(notification) {
        if (!notification) return null;
        return `${dayjs.duration((notification.created - new Date().getTime()) / 1000, 'seconds').humanize(true)} (${formatDisplayDateTime(notification.created)})`;
    }

    function getLink(notification) {
        if (notification.type === 'email-note-mention') {
            const message = emailMessages?.find(m => m?.id === notification.conversationId || m?.globalMessageId === notification.conversationId);
            if (!message) return null;
            return `/email/${message.id}`;
        }

        if (notification.type === 'opportunity-mention' || notification.type === 'opportunity-accepted' || notification.type === 'opportunity-rejected' || notification.type === 'opportunity-owner-changed') {
            const opportunity = opportunities.find(o => o.id === notification.opportunityId);
            if (!opportunity) {
                return null;
            }
            return `/opportunities/${opportunity.id}${notification.type === 'opportunity-mention' ? '?forceStep=1' : ''}#${notification.created}`;
        }

        if (notification.type === 'account-mention') {
            const account = accounts.find(a => a.id === notification.accountId);
            return `/accounts/${account.id}/details#${notification.created}`;
        }

        if (notification.type === 'supplier-mention') {
            const supplier = suppliers.find(s => s.id === notification.supplierId);
            return `/suppliers/${supplier.id}/details#${notification.created}`;
        }

        if (notification.type === 'rates-import-parsing-done') {
            return `/rates/import/log#${notification.importRateId}`;
        }

        if (notification.type === 'rates-import-comment') {
            return `/rates/import/log#${notification.importRateId}`;
        }

        if (notification.type === 'tradeline-mention') {
            return `/tradelines/${notification.tradelineId}`;
        }

        if (notification.type === 'rfq-reply') {
            const opportunity = opportunities.find(o => o.id === notification.opportunityId);
            if (!opportunity) {
                return null;
            }
            return `/opportunities/${opportunity.id}#rfqs`;
        }

        if (notification.type === 'reminder-due') {
            const account = accounts.find(a => a.id === notification.accountId);
            if (!account) {
                return null;
            }
            return `/accounts/${account.id}/details#${notification.created}`;
        }

        if (notification.type === 'task-assigned') {
            return `/accounts/${notification.accountId}/details`;
        }
    }

    function markRead(notification) {
        if (notification.read) return;
        onUpdateNotification({ notificationId: notification.id, changes: { read: true } });
        axios.post(`/api/notifications?accesstoken=${accesstoken}`, { id: notification.id, read: true });
    }

    function markAllRead() {
        const unread = notifications.filter(n => !n.read);
        unread.forEach(n => onUpdateNotification({ notificationId: n.id, changes: { read: true } }));
        unread.forEach(n => axios.post(`/api/notifications?accesstoken=${accesstoken}`, { id: n.id, read: true }));
    }

    const hasUnread = notifications.some(n => !n.read);

    const avatarColors = [blue[500], purple[500], orange[500], teal[500], indigo[500], pink[500], green[500], red[400]];
    function getAvatarColor(email) {
        if (!email) return blue[500];
        let hash = 0;
        for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
        return avatarColors[Math.abs(hash) % avatarColors.length];
    }

    function getInitials(email) {
        if (!email) return '?';
        const name = email.split('@')[0];
        const parts = name.split(/[._-]/);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return name.slice(0, 2).toUpperCase();
    }

    function getActionText(notification) {
        if (notification.type === 'opportunity-accepted') return 'accepted your opportunity';
        if (notification.type === 'opportunity-rejected') return 'rejected your opportunity';
        if (notification.type === 'rfq-reply') return 'replied to your rate request';
        if (notification.type === 'rates-import-parsing-done') return 'finished parsing rates';
        if (notification.type === 'reminder-due') return 'reminder is due';
        if (notification.type === 'task-assigned') return 'assigned you a task';
        if (notification.type === 'opportunity-owner-changed') return 'moved an opportunity to you';
        return 'mentioned you';
    }

    function getContentPreview(notification) {
        if (notification.html) {
            const text = notification.html.replace(/<[^>]+>/g, '').trim();
            return text.length > 120 ? text.slice(0, 120) + '...' : text;
        }
        if (notification.type === 'rfq-reply') {
            return `${notification.supplierName || 'A supplier'} replied. You can now move forward with your quote for ${notification.accountName || 'the account'}.`;
        }
        if (notification.type === 'reminder-due') {
            return notification.reminderDescription || '';
        }
        if (notification.type === 'task-assigned') {
            return notification.taskDescription || '';
        }
        if (notification.type === 'rates-import-parsing-done') {
            return `Rates from ${notification.supplier || 'supplier'} are ready.`;
        }
        return null;
    }

    function getContextLabel(notification) {
        if (notification.type === 'opportunity-mention' || notification.type === 'opportunity-accepted' || notification.type === 'opportunity-rejected' || notification.type === 'opportunity-owner-changed') {
            const opportunity = opportunities.find(o => o.id === notification.opportunityId);
            if (!opportunity) return null;
            const account = accounts.find(a => a.id === opportunity.account?.id);
            return account?.name || 'Opportunity';
        }
        if (notification.type === 'account-mention') {
            const account = accounts.find(a => a.id === notification.accountId);
            return account?.name || 'Account';
        }
        if (notification.type === 'supplier-mention') {
            const supplier = suppliers.find(s => s.id === notification.supplierId);
            return supplier?.name || 'Supplier';
        }
        if (notification.type === 'tradeline-mention') {
            const tradeline = tradelines.find(t => t.id === notification.tradelineId);
            return tradeline?.title || 'Tradeline';
        }
        if (notification.type === 'email-note-mention') {
            const message = emailMessages?.find(m => m?.id === notification.conversationId || m?.globalMessageId === notification.conversationId);
            return message?.subject ? `Email: ${message.subject}` : 'Email note';
        }
        if (notification.type === 'rates-import-comment' || notification.type === 'rates-import-parsing-done') {
            return 'Rates import';
        }
        if (notification.type === 'rfq-reply') {
            return notification.accountName || 'Rate request';
        }
        if (notification.type === 'reminder-due') {
            return notification.accountName || 'Reminder';
        }
        if (notification.type === 'task-assigned') {
            return notification.accountName || 'Task';
        }
        return null;
    }

    const senderEmail = (notification) => notification.createdBy || notification.supplierName || null;

    return (
        <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant='subtitle1' sx={{ flex: 1, fontWeight: 600, fontSize: '15px' }}>Notifications{hasUnread ? ` (${notifications.filter(n => !n.read).length})` : ''}</Typography>
                {hasUnread && (
                    <Button size='small' sx={{ textTransform: 'none', fontSize: '12px', color: blue[600] }} onClick={markAllRead}>
                        Mark all read
                    </Button>
                )}
            </Box>
            {_(notifications).sortBy(n => n.created).reverse().map(notification => {
                if (!notification) return null;
                const email = senderEmail(notification);
                const contentPreview = getContentPreview(notification);
                const contextLabel = getContextLabel(notification);

                return (
                    <Box
                        key={notification.id || notification.created}
                        onClick={() => {
                            onClose();
                            markRead(notification);
                            const link = getLink(notification);
                            if (link) navigate(link);
                        }}
                        sx={{
                            display: 'flex', gap: 1.5, px: 2, py: 1.5,
                            cursor: 'pointer',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: notification.read ? 'transparent' : alpha(blue[500], 0.04),
                            '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.04) },
                        }}
                    >
                        <Avatar sx={{ width: 36, height: 36, fontSize: '13px', fontWeight: 600, bgcolor: getAvatarColor(email), mt: 0.25 }}>
                            {getInitials(email)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant='body2' sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }}>
                                    {email ? email.split('@')[0] : 'System'}
                                </Typography>
                                <Typography variant='caption' sx={{ color: theme.palette.text.secondary, fontSize: '11px' }}>
                                    {getActionText(notification)}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                <Typography variant='caption' sx={{ color: theme.palette.text.disabled, fontSize: '11px', whiteSpace: 'nowrap' }}>
                                    {dayjs(notification.created).fromNow()}
                                </Typography>
                                {!notification.read && (
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: blue[500], ml: 0.5, flexShrink: 0 }} />
                                )}
                            </Box>
                            {contentPreview && (
                                <Typography variant='body2' sx={{ fontSize: '12px', color: theme.palette.text.secondary, mt: 0.5, lineHeight: 1.4,
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {contentPreview}
                                </Typography>
                            )}
                            {contextLabel && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                                    <Box sx={{ fontSize: '11px', color: theme.palette.text.disabled }}>
                                        {getIcon(notification)}
                                    </Box>
                                    <Typography variant='caption' sx={{ color: theme.palette.text.disabled, fontSize: '11px' }}>
                                        {contextLabel}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            }).value()}
        </Box>
    )
}
